import React, {useCallback, useEffect, useState} from "react";
import styled from "styled-components";
import {useTranslation} from "react-i18next";
import {LANGUAGE_PATH, useMediaQuery} from "modules";
import {TextField, InputLabel} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {userPreregistration} from "modules/actions";
import {isPreRegistered} from "modules/selectors";
import {Exist} from "components";
import flagIcon from "apps/DRAW_PREDICTOR/assets/images/icons/flagIcon.svg";
import preRegistrationBanner from "apps/DRAW_PREDICTOR/assets/images/icons/preRegistrationBanner.svg";
import gatesIcon from "apps/DRAW_PREDICTOR/assets/images/icons/gatesIcon.svg";
import preregistrationMobileBanner from "apps/DRAW_PREDICTOR/assets/images/icons/preregistrationMobileBanner.svg";
import {media} from "assets/css/media";
import {fetchSettingsJSON} from "apps/DRAW_PREDICTOR/modules/actions";
import {PreRegistrationTimer, PrimaryButton} from "apps/DRAW_PREDICTOR/components";

const PreregistrationWrapper = styled.div`
	width: 100%;
	height: 100%;
	background-size: cover;
	background: linear-gradient(180deg, #8a1538 -205.33%, #d20a46 186.82%);
	box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
	border-radius: 10px;
	font-family: "Qatar2022", sans-serif;
	margin: 20px auto 48px;
	padding: 0 20px 30px 20px;
	@media (max-width: ${media.mobile}) {
		margin-top: 0;
	}
`;

const Title = styled.h3`
	color: #dfce6d;
	font-size: 20px;
	font-weight: 900;
	text-transform: uppercase;
	font-style: italic;
`;

const SubTitle = styled.div`
	color: #ffffff;
	font-size: 30px;
	font-weight: 900;
	line-height: 120%;
	margin: 10px 0;
	@media (max-width: ${media.mobile}) {
		font-size: 20px;
		line-height: 140%;
	}
`;

const Description = styled.div`
	color: #ffffff;
	font-family: "Graphik";
	font-weight: 500;
	font-size: 20px;
	line-height: 120%;
	padding-inline-end: 5%;
	margin-bottom: 15px;

	@media (max-width: ${media.mobile}) {
		font-weight: 400;
		font-size: 14px;
		line-height: 160%;
		&.hidden-mobile {
			display: none;
		}
	}
`;

const InputLabelStyled = styled(InputLabel)`
	color: #ffffff !important;
	font-size: 16px;
	margin-top: 10px;
	font-family: var(--fontFamilyBase) !important;
	font-weight: 500;
	line-height: 160%;
	margin-bottom: 8px;
`;

const InputStyled = styled(TextField)`
	width: 100%;
	max-width: 425px;
	height: 38px !important;
	margin-top: 10px;
	input {
		color: #ffffff;
		padding-inline-start: 5px;
		height: 26px;
		font-size: 16px;
		line-height: 160%;
		font-family: var(--fontFamilyBase);
		background: rgba(255, 255, 255, 0.1);
		border-radius: 5px 5px 0 0;
		&::placeholder {
			color: #ffffff !important;
		}
	}
	.MuiInput-root {
		font-family: var(--fontFamilyBase);
		&:before {
			border-color: #00b8ff !important;
		}
	}
`;

const ButtonStyled = styled(PrimaryButton)`
	margin-top: 10px;
	font-size: 16px;
	color: #fff;
	font-family: var(--fontFamilyBase);
	border-radius: 50px;
	height: 40px;
	font-weight: 500;
	&:disabled {
		background: rgba(255, 255, 255, 0.3);
		cursor: default;
	}
`;

const ImagesWrapper = styled.div`
	display: flex;
	justify-content: space-between;
	margin-bottom: 40px;
	position: relative;
	overflow: hidden;
	align-items: flex-start;
	.flag {
		margin-top: 10px;
	}
`;

const GatesIcon = styled.img`
	display: block;
`;

const MobileBanner = styled.div`
	width: 100%;
	height: 120px;
	background: url(${preregistrationMobileBanner}) top center no-repeat;
	background-size: contain;
`;

const DescriptionWrapper = styled.div`
	display: flex;
	justify-content: space-between;
`;

interface IProps {
	title: string;
	subTitle: string;
}

export const PreRegistration: React.FC<IProps> = ({title, subTitle}) => {
	const dispatch = useDispatch();
	const {t} = useTranslation(LANGUAGE_PATH);
	const [emailField, seEmail] = useState("");
	const preRegistered = useSelector(isPreRegistered);
	const isTablet = useMediaQuery(`(max-width: 990px)`);

	const descriptionText = preRegistered ? t(subTitle) : t("landing.page.information");
	const subtitle = preRegistered ? t(title) : t("landing.page.header");
	const descClassName = preRegistered ? "hidden-mobile" : "";

	useEffect(() => {
		dispatch(fetchSettingsJSON());
	}, [dispatch]);

	const handleSubmit = useCallback(
		(e: React.SyntheticEvent<HTMLFormElement>) => {
			e.preventDefault();
			e.stopPropagation();
			dispatch(userPreregistration({email: emailField}));
		},
		[dispatch, emailField]
	);

	const handleChangeEmail = useCallback(
		(e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const {value} = e.currentTarget;
			seEmail(value);
		},
		[seEmail]
	);

	return (
		<PreregistrationWrapper>
			<ImagesWrapper>
				<Exist when={!isTablet}>
					<img src={flagIcon} alt={"Flag"} className="flag" />
					<img src={preRegistrationBanner} alt={"Banner"} />
					<GatesIcon src={gatesIcon} alt={"Gates"} />
				</Exist>
				<Exist when={isTablet}>
					<MobileBanner />
				</Exist>
			</ImagesWrapper>
			<Title>{t("landing.page.game_name")}</Title>
			<SubTitle>{subtitle}</SubTitle>
			<DescriptionWrapper>
				<Description className={descClassName}>{descriptionText}</Description>
				<Exist when={preRegistered}>
					<PreRegistrationTimer />
				</Exist>
			</DescriptionWrapper>
			<Exist when={!preRegistered}>
				<form onSubmit={handleSubmit}>
					<InputLabelStyled>{t("landing.page.pre_register_header")}</InputLabelStyled>
					<InputStyled
						type="email"
						required={true}
						variant="standard"
						placeholder={t("landing.page.email_placeholder")}
						onChange={handleChangeEmail}
					/>
					<ButtonStyled type={"submit"} disabled={!emailField}>
						{t("landing.page.submit")}
					</ButtonStyled>
				</form>
			</Exist>
		</PreregistrationWrapper>
	);
};
